<template>
  <div v-if="show" class="ob-reminder" :class="`is-${tone}`">
    <i class="ph-duotone" :class="icon"></i>
    <div class="vp-min-w-0 flex-grow-1">
      <div class="ob-reminder-title">{{ title }}</div>
      <div class="ob-reminder-text">{{ text }}</div>
    </div>
    <router-link to="/onboarding" class="vp-btn vp-btn-primary vp-btn-sm">
      {{ $t('onboarding.reminder.continue') }}
    </router-link>
  </div>
</template>

<script setup>
/**
 * A standing reminder that registration is unfinished.
 *
 * The flow is otherwise unreachable once a supplier navigates away from it: there is no
 * sidebar entry, because the whole thing disappears the moment they are verified and a
 * menu item that vanishes is worse than none. This bar is how they get back.
 *
 * Hidden on the registration screens themselves, where it would only repeat what is
 * already on the page.
 */

// 1. Vue core
import { computed, onMounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useStore } from 'vuex'
import { useI18n } from 'vue-i18n'

// 2. Composables
import { useOnboardingDraft } from '@/composables/useOnboardingDraft'
import { useOnboardingGate } from '@/composables/useOnboardingGate'
import { useFeatureFlags } from '@/composables/useFeatureFlags'

// 3. Constants
import { ONBOARDING_STATUS } from '@/constants/onboarding'

const route = useRoute()
const store = useStore()
const { t } = useI18n()

const { draft, status, load, isRegistrationStarted } = useOnboardingDraft()
const { isUnfinished } = useOnboardingGate()

const isSuperAdmin = computed(() => store.getters['auth/currentUser']?.isSuperAdmin === true)

const { features } = useFeatureFlags()

const show = computed(() =>
  features.onboardingEnforcement &&
  isRegistrationStarted.value &&
  !isSuperAdmin.value &&
  !route.path.startsWith('/onboarding') &&
  Boolean(draft.value) &&
  isUnfinished(status.value)
)

/** A returned draft is a different message from an unfinished one, and more urgent. */
const needsMoreInfo = computed(() => status.value === ONBOARDING_STATUS.MORE_INFO_REQUESTED)

const tone = computed(() => (needsMoreInfo.value ? 'warning' : 'info'))
const icon = computed(() => (needsMoreInfo.value ? 'ph-chat-circle-text' : 'ph-clipboard-text'))
const title = computed(() =>
  needsMoreInfo.value ? t('onboarding.reminder.moreInfoTitle') : t('onboarding.reminder.title')
)
const text = computed(() =>
  needsMoreInfo.value ? t('onboarding.reminder.moreInfoText') : t('onboarding.reminder.text')
)

// Loaded once per session, not per navigation: the status only changes when the supplier
// acts on it, and the onboarding screens refresh the same shared draft themselves. Skipped
// entirely while enforcement is off, so a dormant feature costs no request.
onMounted(() => {
  if (features.onboardingEnforcement) load()
})

// A super admin has no registration of their own; impersonation switches whose it is.
watch(isSuperAdmin, () => {
  if (features.onboardingEnforcement) load(true)
})
</script>

<style scoped>
.ob-reminder {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 16px;
  margin-bottom: 16px;
  border-radius: 10px;
  border: 1px solid var(--vp-info);
  background: var(--vp-light-info);
}

.ob-reminder.is-warning {
  border-color: var(--vp-warning);
  background: var(--vp-light-warning);
}

.ob-reminder > i {
  font-size: 1.25rem;
  flex-shrink: 0;
}

.ob-reminder-title {
  font-size: 0.86rem;
  font-weight: 600;
}

.ob-reminder-text {
  font-size: 0.78rem;
}

@media (max-width: 575.98px) {
  .ob-reminder {
    flex-wrap: wrap;
  }
}
</style>
